import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import logoHeader from './images/logo_header.png';
import logoMark from './images/logo.png';

const title = 'Eztrellas Solutions';
const email = 'info@eztrellas.com';
const phone = '+44 7759 504 640';

const navItems = [
  ['Home', '/'],
  ['Services', '/services'],
  ['Packages', '/packages'],
  ['Examples', '/examples'],
  ['Free Review', '/free-review'],
  ['About', '/about'],
  ['Contact', '/contact'],
];

const services = [
  {
    title: 'Websites & Landing Pages',
    text: 'Simple, mobile-friendly pages for small businesses, campaigns, and enquiries.',
  },
  {
    title: 'Business Email Setup',
    text: 'Professional email using Google Workspace or Microsoft 365, with domain and DNS configuration.',
  },
  {
    title: 'Email Marketing Setup',
    text: 'Mailchimp, Brevo, MailerLite or SendGrid setup, including templates, signup forms, and lists.',
  },
  {
    title: 'Customer Follow-up Automation',
    text: 'Simple enquiry responses, quote follow-ups, review requests, and welcome emails.',
  },
];

const serviceGroups = [
  {
    title: 'Website & Digital Presence',
    intro: 'For small businesses that need a clear, trustworthy online presence without a large agency project.',
    includes: [
      'One-page business websites',
      'Landing pages',
      'Website refreshes',
      'Contact forms',
      'Mobile-friendly layouts',
      'Basic SEO setup',
      'Google Business Profile review',
    ],
    bestFor: [
      'Businesses with no website',
      'Outdated websites',
      'Businesses relying only on social media',
      'Local businesses needing more trust and enquiries',
    ],
  },
  {
    title: 'Business Email & Domain Setup',
    intro: 'For businesses that want professional email, reliable routing, and the basic domain records set correctly.',
    includes: [
      'Google Workspace setup',
      'Microsoft 365 setup',
      'Domain email setup',
      'DNS records',
      'SPF, DKIM, and DMARC basics',
      'Email signatures',
      'Contact form routing',
    ],
    bestFor: [
      'Businesses using personal email addresses',
      'Businesses struggling with email setup',
      'New businesses needing a professional setup',
    ],
  },
  {
    title: 'Email Marketing Setup',
    intro: 'For businesses ready to send useful newsletters, promotions, or updates without getting stuck in the platform.',
    includes: [
      'Mailchimp, Brevo, MailerLite or SendGrid setup',
      'Audience and list setup',
      'Signup forms',
      'Branded email templates',
      'Newsletter setup',
      'Unsubscribe and consent basics',
      'Test campaigns',
    ],
    bestFor: [
      'Businesses that want to stay in touch with customers',
      'Businesses with customer lists but no campaign process',
      'Businesses wanting simple newsletters or promotions',
    ],
  },
  {
    title: 'Customer Follow-up Automation',
    intro: 'For businesses that need a practical way to respond, chase, remind, and ask for reviews.',
    includes: [
      'Enquiry auto-response',
      'Quote follow-up emails',
      'Welcome emails',
      'Review request emails',
      'Appointment reminders',
      'Re-engagement campaigns',
    ],
    bestFor: [
      'Businesses that miss enquiries',
      'Businesses that manually chase customers',
      'Businesses that want repeat customers and better reviews',
    ],
  },
  {
    title: 'Ongoing Support',
    intro: 'For businesses that want light monthly help without hiring in-house or committing to a large retainer.',
    includes: [
      'Website updates',
      'Monthly email campaign support',
      'Small automation changes',
      'List clean-up',
      'Simple reporting',
      'Content tweaks',
      'Platform support',
    ],
    bestFor: [
      'Businesses that want help without hiring in-house',
      'Businesses that want a reliable monthly digital support partner',
    ],
  },
];

const packages = [
  {
    title: 'Digital Starter',
    price: 'From \u00a3299 setup',
    intro: 'For businesses that need a professional online presence.',
    includes: [
      'One-page website or landing page',
      'Contact form',
      'Call or WhatsApp button',
      'Basic SEO structure',
      'Map and opening hours',
      'Basic Google Business Profile review',
    ],
    optional: 'Hosting and light support from \u00a325/month',
  },
  {
    title: 'Communication Starter',
    price: 'From \u00a3399 setup',
    intro: 'For businesses that need proper email and customer communication.',
    includes: [
      'Business email setup support',
      'Mailchimp, Brevo or MailerLite setup',
      'Signup form',
      'Branded email template',
      'Enquiry auto-response',
      'One simple follow-up journey',
    ],
    optional: 'Monthly support from \u00a350/month',
  },
  {
    title: 'Website + Communication Setup',
    price: 'From \u00a3599 setup',
    intro: 'The strongest bundle for a complete practical setup.',
    includes: [
      'One-page website',
      'Business email and domain setup support',
      'Email marketing platform setup',
      'Contact form connected to inbox or list',
      'Enquiry auto-response',
      'Review request email',
      'Handover guide',
    ],
    optional: 'Ongoing support from \u00a375/month',
  },
  {
    title: 'Ongoing Digital Support',
    price: 'From \u00a375-\u00a3150/month',
    intro: 'For clients who want reliable support after setup.',
    includes: [
      'Small website updates',
      'Email campaign help',
      'Simple reporting',
      'Automation tweaks',
      'Platform support',
    ],
    optional: 'Typical limits: up to 1 campaign/month, up to 1 hour support/month, small edits only. Larger work quoted separately.',
  },
  {
    title: 'Others',
    price: 'From \u00a3100/man-hour',
    intro: 'For clients who want reliable support after setup.',
    includes: [
      'Database architecture',
      'IT consultancy',
      'Data cleansing',
      'Backup solutions',
      'Network setup',
      'Custom projects',
    ],
    optional: 'Let\'s discuss your specific needs and I can provide a custom quote based on the work involved.',
  },
];

const examples = [
  {
    title: 'Local salon website concept',
    problem: 'Only Instagram, no proper website for services, prices, and booking enquiries.',
    solution: 'A one-page site with services, prices, booking CTA, review request email, and contact form.',
    value: 'Makes the business easier to trust and gives customers a clearer next step.',
  },
  {
    title: 'Tutor enquiry journey',
    problem: 'New enquiries handled manually with no consistent response or tracking.',
    solution: 'Landing page, auto-response email, simple follow-up email, and clearer lead capture.',
    value: 'Reduces missed enquiries and makes the first reply feel professional.',
  },
  {
    title: 'Tradesperson quote follow-up',
    problem: 'Quotes sent by email but rarely chased afterwards.',
    solution: 'Simple follow-up sequence with a helpful reminder and review request after completed jobs.',
    value: 'Improves the chance of conversion and encourages more customer reviews.',
  },
];

const problems = [
  'Still using a personal Gmail or Hotmail address?',
  'No proper website, or one that feels outdated?',
  'Enquiries coming in but no clear follow-up process?',
  'Want to send newsletters but do not know where to start?',
  'Tried Mailchimp or Google Workspace but got stuck?',
];

function Header() {
  return (
    <header className="site-header">
      <Link className="brand" to="/" aria-label={`${title} home`}>
        <img src={logoHeader} alt={title} />
      </Link>
      <nav className="nav" aria-label="Main navigation">
        {navItems.map(([label, href]) => (
          <NavLink key={href} to={href} end={href === '/'}>
            {label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div>
        <img src={logoMark} alt="" aria-hidden="true" />
        <span>{title}</span>
      </div>
      <p>
        Design Your Universe
        &nbsp;|&nbsp;
        <a href={`mailto:${email}`}>{email}</a>
        &nbsp;|&nbsp;
        <a href={`tel:${phone}`} aria-label="Call us">{phone}</a>
      </p>
    </footer >
  );
}

function CtaButtons({ review = false }) {
  return (
    <div className="cta-row">
      <Link className="button primary" to={review ? '/free-review' : '/contact'}>
        {review ? 'Request a free review' : 'Book a free chat'}
      </Link>
      <Link className="button secondary" to="/services">
        View services
      </Link>
    </div>
  );
}

function SectionIntro({ eyebrow, title: heading, children }) {
  return (
    <div className="section-intro">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h2>{heading}</h2>
      {children && <p>{children}</p>}
    </div>
  );
}

function CardGrid({ children, className = '' }) {
  return <div className={`card-grid ${className}`}>{children}</div>;
}

function ServiceCard({ item }) {
  return (
    <article className="card">
      <h3>{item.title}</h3>
      <p>{item.text}</p>
    </article>
  );
}

function PackageCard({ item, compact = false }) {
  return (
    <article className="card package-card">
      <div>
        <h3>{item.title}</h3>
        <p>{item.intro}</p>
      </div>
      <strong>{item.price}</strong>
      {!compact && (
        <>
          <ul className="check-list">
            {item.includes.map((include) => (
              <li key={include}>{include}</li>
            ))}
          </ul>
          <p className="muted">{item.optional}</p>
        </>
      )}
    </article>
  );
}

function ContactForm({ review = false }) {
  return (
    <form className="contact-form" action={`mailto:${email}`} method="post" encType="text/plain">
      <label>
        Name
        <input type="text" name="name" required />
      </label>
      <label>
        Business name
        <input type="text" name="business_name" />
      </label>
      <label>
        Email
        <input type="email" name="email" required />
      </label>
      <label>
        Website or social link
        <input type="url" name="website_or_social_link" placeholder="https://" />
      </label>
      {!review && (
        <label>
          Service interested in
          <select name="service_interested_in" defaultValue="">
            <option value="" disabled>Select one</option>
            <option>Website & Digital Presence</option>
            <option>Business Email & Domain Setup</option>
            <option>Email Marketing Setup</option>
            <option>Customer Follow-up Automation</option>
            <option>Ongoing Support</option>
            <option>Not sure yet, I want to discuss options</option>
          </select>
        </label>
      )}
      <label className="full-width">
        {review ? 'What do you need help with?' : 'Message'}
        <textarea name={review ? 'what_they_need_help_with' : 'message'} rows="5" required />
      </label>
      {review && (
        <label className="checkbox full-width">
          <input type="checkbox" name="permission_to_contact" required />
          <span>I give permission for Eztrellas Solutions to contact me about this review.</span>
        </label>
      )}
      <button className="button primary" type="submit">
        {review ? 'Get a free digital setup review' : 'Send enquiry'}
      </button>
    </form>
  );
}

function HomePage() {
  return (
    <>
      <section className="hero-section">
        <div className="hero-copy">
          <p className="eyebrow">Simple digital systems for small businesses</p>
          <h1>Practical digital setup for small businesses</h1>
          <p>
            Websites, business email, email marketing, and simple customer follow-up systems
            built to help small businesses look professional, capture enquiries, and stay in
            touch with customers.
          </p>
          <CtaButtons />
        </div>
        <div className="hero-panel" aria-label="Digital setup checklist">
          <img src={logoMark} alt="" aria-hidden="true" />
          <ul>
            <li>Professional online presence</li>
            <li>Business email that works</li>
            <li>Customer follow-up made simple</li>
          </ul>
        </div>
      </section>

      <section className="band">
        <SectionIntro eyebrow="Problems solved" title="Does this sound familiar?" />
        <div className="problem-list">
          {problems.map((problem) => (
            <p key={problem}>{problem}</p>
          ))}
        </div>
      </section>

      <section>
        <SectionIntro title="Services">
          Practical setup across the basics small businesses need most.
        </SectionIntro>
        <CardGrid>
          {services.map((service) => (
            <ServiceCard key={service.title} item={service} />
          ))}
        </CardGrid>
      </section>

      <section className="split-section">
        <div>
          <p className="eyebrow">Who it is for</p>
          <h2>Built for small teams that need useful help, not agency overhead.</h2>
        </div>
        <p>
          Ideal for local service businesses, sole traders, independent shops, tutors,
          consultants, clinics, salons, and small teams that need practical digital support
          without agency-level cost.
        </p>
      </section>

      <section className="band">
        <SectionIntro title="Simple packages">
          Clear starting points, with the details adjusted around your current setup.
        </SectionIntro>
        <CardGrid className="three-col">
          {packages.slice(0, 3).map((item) => (
            <PackageCard key={item.title} item={item} compact />
          ))}
        </CardGrid>
        <div className="centered-action">
          <Link className="button secondary" to="/packages">Compare packages</Link>
        </div>
      </section>

      <section className="split-section">
        <div>
          <p className="eyebrow">Why work with me</p>
          <h2>Technical setup with marketing communication experience.</h2>
        </div>
        <div>
          <p>
            I combine technical setup with marketing communication experience, so I do not
            just make things look nice. I help put the basic systems in place to capture
            enquiries, follow up with customers, and keep things manageable.
          </p>
          <ul className="check-list two-col">
            <li>Practical, non-agency approach</li>
            <li>Clear handover</li>
            <li>Simple systems SMBs can actually use</li>
            <li>Technical setup experience</li>
          </ul>
        </div>
      </section>

      <section>
        <SectionIntro title="Simple process" />
        <ol className="process-list">
          <li>Free discovery chat</li>
          <li>Review your current setup</li>
          <li>Recommend the simplest solution</li>
          <li>Build and configure</li>
          <li>Launch, handover, and support</li>
        </ol>
      </section>

      <section className="cta-band">
        <h2>Not sure what your business needs yet?</h2>
        <p>
          I can review your current website, email, and customer communication setup,
          then suggest the simplest next step.
        </p>
        <CtaButtons review />
      </section>
    </>
  );
}

function ServicesPage() {
  return (
    <>
      <PageHero
        eyebrow="Services"
        title="Websites, email, and follow-up systems that small businesses can actually use."
        text="Choose one focused setup project, or combine services into a practical launch package."
      />
      <section className="service-detail-list">
        {serviceGroups.map((group) => (
          <article className="detail-block" key={group.title}>
            <div>
              <h2>{group.title}</h2>
              <p>{group.intro}</p>
            </div>
            <div className="detail-columns">
              <div>
                <h3>Includes</h3>
                <ul className="check-list">
                  {group.includes.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3>Best for</h3>
                <ul className="plain-list">
                  {group.bestFor.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </article>
        ))}
      </section>
    </>
  );
}

function PackagesPage() {
  return (
    <>
      <PageHero
        eyebrow="Packages"
        title="Clear starting points with from pricing."
        text="Pricing depends on your existing setup, platforms, content readiness, and how much support you need after launch."
      />
      <section>
        <CardGrid className="two-col-grid">
          {packages.map((item) => (
            <PackageCard key={item.title} item={item} />
          ))}
        </CardGrid>
      </section>
      <section className="note-band">
        <h2>Limits are agreed before work starts.</h2>
        <p>
          Monthly support is intended for light ongoing help such as one campaign per month,
          up to one hour of support, small content edits, and simple reporting. Larger changes
          are quoted separately.
        </p>
      </section>
    </>
  );
}

function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About"
        title="Practical digital support from someone who understands both technology and communication."
        text="I help small businesses set up the essential systems that make them look professional, capture enquiries, and keep in touch with customers."
      />
      <section className="split-section">
        <div>
          <p className="eyebrow">Who I am</p>
          <h2>I am Tung, a digital messaging and marketing technology specialist based in London.</h2>
        </div>
        <div>
          <p>
            I help small businesses set up practical digital systems, from websites and
            business email to email marketing and customer follow-up automation.
          </p>
          <p>
            My background covers digital messaging, CRM, MarTech, large-scale customer
            communication, and hands-on technical setup. That mix helps me explain options
            clearly and build systems that are manageable after handover.
          </p>
        </div>
      </section>
      <section className="band">
        <SectionIntro title="My approach" />
        <CardGrid className="three-col">
          {[
            'Simple before complex',
            'Practical setup',
            'Clear handover',
            'Affordable for small businesses',
            'No unnecessary jargon',
            'Support that fits real workflows',
          ].map((item) => (
            <article className="card small-card" key={item}>
              <h3>{item}</h3>
            </article>
          ))}
        </CardGrid>
      </section>
    </>
  );
}

function ExamplesPage() {
  return (
    <>
      <PageHero
        eyebrow="Examples"
        title="Sample concepts for common small business problems."
        text="These are example project shapes to show how the services can work in practice. They can be adapted for real client needs."
      />
      <section>
        <CardGrid className="three-col">
          {examples.map((item) => (
            <article className="card example-card" key={item.title}>
              <p className="tag">Sample concept</p>
              <h2>{item.title}</h2>
              <h3>Problem</h3>
              <p>{item.problem}</p>
              <h3>Solution</h3>
              <p>{item.solution}</p>
              <h3>Added value</h3>
              <p>{item.value}</p>
            </article>
          ))}
        </CardGrid>
      </section>
    </>
  );
}

function ReviewPage() {
  return (
    <>
      <PageHero
        eyebrow="Free review"
        title="Free Small Business Digital Setup Review"
        text="A low-pressure way to understand what needs fixing first across your website, email, enquiries, and customer communication."
      />
      <section className="split-section align-start">
        <div>
          <h2>What I review</h2>
          <ul className="check-list">
            <li>Website</li>
            <li>Business email</li>
            <li>Google Business Profile</li>
            <li>Enquiry process</li>
            <li>Email marketing readiness</li>
          </ul>
          <h2>What you get</h2>
          <ul className="check-list">
            <li>3 quick improvement suggestions</li>
            <li>A recommended next step</li>
            <li>An optional quote if you want help implementing it</li>
          </ul>
        </div>
        <ContactForm review />
      </section>
    </>
  );
}

function ContactPage() {
  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Tell me what you are trying to set up."
        text="Based in London and working with small businesses across the UK. I usually reply within 1-2 working days."
      />
      <section className="split-section align-start">
        <div className="contact-details">
          <h2>Contact details</h2>
          <p>Email: <a href={`mailto:${email}`}>{email}</a></p>
          <p>Location: London / UK</p>
        </div>
        <ContactForm />
      </section>
    </>
  );
}

function PageHero({ eyebrow, title: heading, text }) {
  return (
    <section className="page-hero">
      <p className="eyebrow">{eyebrow}</p>
      <h1>{heading}</h1>
      <p>{text}</p>
    </section>
  );
}

function Revamp({ page = 'home' }) {
  const pages = {
    home: <HomePage />,
    services: <ServicesPage />,
    packages: <PackagesPage />,
    examples: <ExamplesPage />,
    review: <ReviewPage />,
    about: <AboutPage />,
    contact: <ContactPage />,
  };

  return (
    <>
      <Header />
      <main>{pages[page] || pages.home}</main>
      <Footer />
    </>
  );
}

export default Revamp;
